import { useState, useMemo } from 'react'
import { useUsers } from '../hooks/useUsers'
import { useEventRisks } from '../hooks/useRisks'
import {
  ModalShell, Field, inputClass, CancelButton, PrimaryButton, DangerButton, ServerError, FormStatus
} from './modal-form'
import type { EventRisk, EventRiskBody } from '../types'

// RiskEditor - per-row create/edit modal.
// inherent + residual likelihood & severity render as 1..5 chip pickers with
// a live computed score chip alongside. Soft-warn (FormStatus tone='info')
// when residual > inherent rather than blocking save - manager judgement
// reigns per spec 04 §Open questions.

interface Props {
  eventId: number
  risk: EventRisk | null
  saving?: boolean
  deleting?: boolean
  serverError?: Error | null
  onClose: () => void
  onSave: (body: EventRiskBody) => void
  onDelete?: () => void
}

export function RiskEditor({ eventId, risk, saving, deleting, serverError, onClose, onSave, onDelete }: Props) {
  const isEditing = risk != null
  const { data: users } = useUsers()
  const { data: existing } = useEventRisks(eventId)

  // Build category suggestions from existing rows (free-text combobox v1).
  const categorySuggestions = useMemo(() => {
    const set = new Set<string>()
    for (const r of existing ?? []) set.add(r.category)
    return Array.from(set).sort()
  }, [existing])

  const [hazard, setHazard] = useState(risk?.hazard ?? '')
  const [category, setCategory] = useState(risk?.category ?? 'PTI')
  const [il, setIl] = useState(risk?.inherentLikelihood ?? 3)
  const [is_, setIs] = useState(risk?.inherentSeverity ?? 3)
  const [controls, setControls] = useState(risk?.controls ?? '')
  const [rl, setRl] = useState(risk?.residualLikelihood ?? risk?.inherentLikelihood ?? 2)
  const [rs, setRs] = useState(risk?.residualSeverity ?? risk?.inherentSeverity ?? 2)
  const [ownerUserId, setOwnerUserId] = useState<number | null>(risk?.owner?.id ?? null)
  const [notes, setNotes] = useState(risk?.notes ?? '')

  const trimmedHazard = hazard.trim()
  const trimmedCategory = category.trim()
  const trimmedControls = controls.trim()
  const isValid =
    trimmedHazard.length > 0 &&
    trimmedHazard.length <= 200 &&
    trimmedCategory.length > 0 &&
    trimmedCategory.length <= 80 &&
    trimmedControls.length > 0

  const inherentScore = il * is_
  const residualScore = rl * rs
  const residualHigherThanInherent = residualScore > inherentScore

  function handleSubmit() {
    if (!isValid) return
    onSave({
      hazard: trimmedHazard,
      category: trimmedCategory,
      inherentLikelihood: il,
      inherentSeverity: is_,
      controls,
      residualLikelihood: rl,
      residualSeverity: rs,
      ownerUserId,
      notes: notes.trim().length === 0 ? null : notes,
      sortOrder: risk?.sortOrder ?? 9999,
      sourceLibraryId: risk?.sourceLibraryId ?? null
    })
  }

  const body = (
    <div className="space-y-4">
      <Field label="Hazard">
        <input
          type="text"
          value={hazard}
          onChange={(e) => setHazard(e.target.value)}
          placeholder="Platform-train interface fall"
          maxLength={200}
          className={inputClass(hazard.length > 0 && trimmedHazard.length === 0)}
        />
      </Field>

      <Field label="Category">
        <input
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          maxLength={80}
          list="risk-categories"
          className={inputClass(category.length > 0 && trimmedCategory.length === 0)}
        />
        <datalist id="risk-categories">
          {['PTI', 'Crowd', 'Fire', 'Heritage Operation', 'Public Behaviour', 'Site', 'Catering', 'First Aid', 'Programme']
            .concat(categorySuggestions.filter((s) => !['PTI', 'Crowd', 'Fire', 'Heritage Operation', 'Public Behaviour', 'Site', 'Catering', 'First Aid', 'Programme'].includes(s)))
            .map((c) => (
              <option key={c} value={c} />
            ))}
        </datalist>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
            Inherent risk
          </div>
          <ScorePicker label="Likelihood" value={il} onChange={setIl} />
          <ScorePicker label="Severity" value={is_} onChange={setIs} />
          <ScoreChip score={inherentScore} label="Inherent score" />
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
            Residual risk (after controls)
          </div>
          <ScorePicker label="Likelihood" value={rl} onChange={setRl} />
          <ScorePicker label="Severity" value={rs} onChange={setRs} />
          <ScoreChip score={residualScore} label="Residual score" />
        </div>
      </div>

      {residualHigherThanInherent && (
        <FormStatus tone="info">
          Residual ({residualScore}) is higher than inherent ({inherentScore}).
          That's unusual — check the L/S values.
        </FormStatus>
      )}

      <Field label="Controls in place">
        <textarea
          value={controls}
          onChange={(e) => setControls(e.target.value)}
          rows={3}
          placeholder="Yellow line; staff briefing; tannoy reminders…"
          className={inputClass(controls.length > 0 && trimmedControls.length === 0)}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Owner (optional)">
          <select
            value={ownerUserId ?? ''}
            onChange={(e) => setOwnerUserId(e.target.value ? Number(e.target.value) : null)}
            className={inputClass(false)}
          >
            <option value="">— Unassigned —</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name ?? `User ${u.id}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Notes (optional)">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass(false)}
          />
        </Field>
      </div>

      {serverError && <ServerError title="Could not save" error={serverError} />}
    </div>
  )

  const footer = (
    <>
      {isEditing && onDelete && (
        <DangerButton onClick={onDelete} disabled={!!deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </DangerButton>
      )}
      <CancelButton onClick={onClose} disabled={!!saving} />
      <PrimaryButton onClick={handleSubmit} disabled={!isValid || !!saving}>
        {saving ? 'Saving…' : isEditing ? 'Save' : 'Add risk'}
      </PrimaryButton>
    </>
  )

  return (
    <ModalShell
      title={isEditing ? 'Edit risk' : 'Add risk'}
      onClose={onClose}
      body={body}
      footer={footer}
      size="2xl"
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    />
  )
}

// 1-5 chip picker for likelihood / severity.
function ScorePicker({
  label, value, onChange
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div>
      <div className="text-xs text-ink-soft mb-1">{label}</div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            className={
              'w-9 h-9 rounded text-sm font-semibold border transition-colors ' +
              (value === n
                ? 'bg-green text-parchment border-green'
                : 'bg-white text-ink-soft border-line hover:border-green')
            }
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  )
}

// Colour-banded score chip. Bands per spec 04:
// 1-4 green, 5-9 slate, 10-14 parchment-dark, 15-19 brass, 20-25 oxblood.
function ScoreChip({ score, label }: { score: number; label: string }) {
  const band = scoreBand(score)
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="text-xs text-ink-soft">{label}</span>
      <span
        className="px-2 py-0.5 rounded text-sm font-bold font-mono"
        style={{ background: band.bg, color: band.fg }}
      >
        {score}
      </span>
    </div>
  )
}

export function scoreBand(score: number): { bg: string; fg: string; tone: 'green' | 'slate' | 'parchment' | 'brass' | 'oxblood' } {
  if (score <= 4) return { bg: '#dcebe5', fg: '#0a4a3a', tone: 'green' }
  if (score <= 9) return { bg: '#e0e7ec', fg: '#3d5a6c', tone: 'slate' }
  if (score <= 14) return { bg: '#ece6db', fg: '#5a5a5a', tone: 'parchment' }
  if (score <= 19) return { bg: '#f5edd5', fg: '#9b7f2a', tone: 'brass' }
  return { bg: '#f8dbdd', fg: '#7a2329', tone: 'oxblood' }
}
